import React, { FC, useEffect } from 'react';
import { Redirect } from 'react-router';
import { connect } from 'react-redux';
import StatusCodeComponentWrapper from '../../hooks/StaticContent/StatusCodeComponentWrapper';
import StateInterface, { BellaThunkDispatch } from '../../redux/types';
import { RedirectResponse } from '../../redux/redirect/types';
import { unsetRedirect as unsetRedirectAction } from '../../redux/redirect/actions';
import usePrevious from '../../hooks/usePrevious';
import useBellaPush from '../../hooks/useBellaHistoryPush';
import { useIsBellaPageType } from '../../hooks/UrlManager/pageTypeHooks';
import { useLogger } from '../../logger/LoggerProvider';

interface RedirectProps {
    redirect: {
        data?: RedirectResponse;
        key: string;
    };
}

interface RedirectDispatchProps {
    unsetRedirect: () => void;
}

export type Props = RedirectProps & RedirectDispatchProps;

/**
 * this is the client-side redirect parser mentioned in docs/redirects.md
 */
export const RouteRedirect: FC<Props> = ({ redirect, unsetRedirect }) => {
    const isRedirect = !!(redirect.key && redirect.data);
    const wasRedirected = usePrevious(isRedirect);
    const isNewRedirect = !wasRedirected && isRedirect;
    const bellaPush = useBellaPush();
    const isBellaPageType = useIsBellaPageType();
    const logger = useLogger();

    const { search } = window.location;

    // in the case of dispatch actions, they are actually the same, but a new function is always created on every render
    // hence we should not include it as the dependency, since it triggers the whole state update and results into infinite update loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => () => unsetRedirect(), [isNewRedirect]);

    if (isNewRedirect && redirect.data) {
        logger.info(`Redirecting Client-Side, reason: ${redirect.data.source}, url: ${redirect.data.targetPath}`);
    }

    return isNewRedirect && redirect.data ? (
        <>
            {isBellaPageType(redirect.data.targetPath) ? (
                bellaPush(redirect.data.targetPath)
            ) : (
                <StatusCodeComponentWrapper code={redirect.data.httpCode}>
                    <Redirect to={search ? `${redirect.data.targetPath}${search}` : redirect.data.targetPath} />;
                </StatusCodeComponentWrapper>
            )}
        </>
    ) : null;
};

const mapStateToProps = (state: StateInterface): RedirectProps => ({
    redirect: state.redirect,
});

const mapDispatchToProps = (dispatch: BellaThunkDispatch): RedirectDispatchProps => ({
    unsetRedirect: () => dispatch(unsetRedirectAction()),
});

const connected = connect(mapStateToProps, mapDispatchToProps)(RouteRedirect);

export default connected;
